import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import zhangjiajieImage from "../assets/zhangjiajie-forest.png";

type TravelPackage = {
  id: string;
  title: string;
  country: string;
  destination?: string;
  summary: string;
  durationLabel: string;
  durationDays?: number;
  topics?: string[];
  price: string;
  oldPrice?: string;
  badge?: string;
  image: string;
};

type TravelTabProps = {
  keyword: string;
  onKeywordChange: (value: string) => void;
  onKeywordSearch: () => void;
  destination: string;
  destinationOptions: string[];
  onDestinationChange: (value: string) => void;
  selectedTopics: string[];
  topicOptions: string[];
  onToggleTopic: (topic: string) => void;
  durationDays: number | null;
  durationOptions: number[];
  onDurationDaysChange: (value: number | null) => void;
  maxPrice: number | null;
  maxPriceLimit: number;
  onMaxPriceChange: (value: number) => void;
  travelPageItems: TravelPackage[];
  travelPage: number;
  travelTotalPages: number;
  setTravelPage: Dispatch<SetStateAction<number>>;
  onSelectPackage: (packageId: string) => void;
  isLoading: boolean;
  loadError: string | null;
  onRetryLoad: () => void;
};

export default function TravelTab({
  keyword,
  onKeywordChange,
  onKeywordSearch,
  destination,
  destinationOptions,
  onDestinationChange,
  selectedTopics,
  topicOptions,
  onToggleTopic,
  durationDays,
  durationOptions,
  onDurationDaysChange,
  maxPrice,
  maxPriceLimit,
  onMaxPriceChange,
  travelPageItems,
  travelPage,
  travelTotalPages,
  setTravelPage,
  onSelectPackage,
  isLoading,
  loadError,
  onRetryLoad
}: TravelTabProps) {
  const effectiveMaxPrice = typeof maxPrice === "number" ? maxPrice : maxPriceLimit;
  const [showAllThemes, setShowAllThemes] = useState(false);

  const normalizedDurationOptions = useMemo(
    () =>
      Array.from(new Set(durationOptions))
        .filter((item) => Number.isFinite(item) && item > 0)
        .sort((a, b) => a - b),
    [durationOptions]
  );
  const hasMoreThemes = topicOptions.length > 8;
  const visibleThemes = showAllThemes ? topicOptions : topicOptions.slice(0, 8);

  useEffect(() => {
    setShowAllThemes(false);
  }, [topicOptions]);

  return (
    <section className="mb-10 space-y-10 md:mb-16">
      <div className="relative overflow-hidden bg-[#0b111a]">
        <div
          className="h-[260px] w-full bg-cover bg-center md:h-[320px]"
          style={{ backgroundImage: `url('${zhangjiajieImage}')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(6,12,20,0.78)] via-[rgba(6,12,20,0.5)] to-transparent" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-white/70">
              Our Packages
            </p>
            <h2 className="mt-3 text-3xl font-semibold text-white md:text-5xl">
              Discover Your Next Journey
            </h2>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-[280px_1fr]">
          <aside className="space-y-8 text-[13px] text-[#6b7280]">
            <div className="border-b border-[#eef2f6] pb-6">
              <p className="text-sm font-semibold text-[#1f2937]">Search trips:</p>
              <form
                className="mt-3 flex items-center gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  onKeywordSearch();
                }}
              >
                <input
                  type="text"
                  value={keyword}
                  onChange={(event) => onKeywordChange(event.target.value)}
                  placeholder="City, title, or keyword"
                  className="w-full rounded-xl border border-[#e5e7eb] bg-transparent px-3 py-2 text-sm text-[#6b7280] outline-none focus:border-[#0f2c6e]"
                />
                <button
                  type="submit"
                  className="rounded-xl bg-[#0f2c6e] px-3 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-white"
                >
                  Search
                </button>
              </form>
            </div>

            <div className="border-b border-[#eef2f6] pb-6">
              <p className="text-sm font-semibold text-[#1f2937]">
                Select your destination:
              </p>
              <div className="relative mt-3">
                <select
                  value={destination}
                  onChange={(event) => onDestinationChange(event.target.value)}
                  className="w-full appearance-none border-b border-[#e5e7eb] bg-transparent pb-2 pr-6 text-sm text-[#6b7280] outline-none"
                >
                  <option value="">All Destinations</option>
                  {destinationOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-[#9aa3af]">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>

            <div className="border-b border-[#eef2f6] pb-6">
              <p className="text-sm font-semibold text-[#1f2937]">Themes:</p>
              {topicOptions.length === 0 ? (
                <p className="mt-3 text-xs text-[#9aa3af]">No themes available.</p>
              ) : (
                <>
                  <div className="mt-4 max-h-44 overflow-y-auto pr-1">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {visibleThemes.map((item) => (
                        <label key={item} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedTopics.includes(item)}
                            onChange={() => onToggleTopic(item)}
                            className="h-3.5 w-3.5 rounded border-[#d1d5db] text-[#0b1c2b]"
                          />
                          <span className="truncate" title={item}>
                            {item}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {hasMoreThemes && (
                    <button
                      type="button"
                      className="mt-3 text-xs font-semibold text-[#0f2c6e]"
                      onClick={() => setShowAllThemes((current) => !current)}
                    >
                      {showAllThemes
                        ? "Collapse themes"
                        : `Show all themes (${topicOptions.length})`}
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="border-b border-[#eef2f6] pb-6">
              <p className="text-sm font-semibold text-[#1f2937]">Duration:</p>
              <div className="relative mt-3">
                <select
                  value={durationDays === null ? "" : String(durationDays)}
                  onChange={(event) =>
                    onDurationDaysChange(
                      event.target.value ? Number.parseInt(event.target.value, 10) : null
                    )
                  }
                  className="w-full appearance-none border-b border-[#e5e7eb] bg-transparent pb-2 pr-6 text-sm text-[#6b7280] outline-none"
                >
                  <option value="">Any duration</option>
                  {normalizedDurationOptions.map((item) => (
                    <option key={item} value={item}>
                      {item} {item === 1 ? "day" : "days"}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-[#9aa3af]">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>

            <div className="border-b border-[#eef2f6] pb-6">
              <div className="flex items-center justify-between text-sm font-semibold text-[#1f2937]">
                <span>Max Price:</span>
                <span className="text-xs font-normal text-[#6b7280]">
                  {Math.round(effectiveMaxPrice)} $
                </span>
              </div>
              <input
                type="range"
                min="0"
                max={Math.max(100, Math.ceil(maxPriceLimit))}
                value={Math.max(
                  0,
                  Math.min(effectiveMaxPrice, Math.ceil(maxPriceLimit))
                )}
                onChange={(event) => onMaxPriceChange(Number(event.target.value))}
                className="mt-4 h-1 w-full cursor-pointer accent-[#0f2c6e]"
              />
            </div>
          </aside>

          <div className="flex flex-col gap-10">
            <div className="rounded-3xl border border-[#eef2f6] bg-white shadow-[0_18px_40px_rgba(15,23,36,0.08)]">
              <div className="border-b border-[#eef2f6] px-6 py-5">
                <p className="text-sm font-semibold text-[#111827]">
                  Featured Tours
                </p>
                <p className="mt-1 text-xs text-[#9aa3af]">
                  Discover our bestselling packages.
                </p>
              </div>

              <div className="p-6">
                {isLoading ? (
                  <div className="rounded-2xl border border-dashed border-[#d7dee8] bg-[#f8fbff] px-6 py-12 text-center text-sm text-[#64748b]">
                    Loading published trips...
                  </div>
                ) : loadError ? (
                  <div className="rounded-2xl border border-[#ffd6d6] bg-[#fff7f7] px-6 py-10 text-center text-sm text-[#b42318]">
                    <p>Failed to load trips from server.</p>
                    <p className="mt-1 text-xs text-[#b54708]">{loadError}</p>
                    <button
                      type="button"
                      onClick={onRetryLoad}
                      className="mt-4 rounded-lg bg-[#111827] px-4 py-2 text-xs font-semibold text-white"
                    >
                      Retry
                    </button>
                  </div>
                ) : travelPageItems.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[#d7dee8] bg-[#f8fbff] px-6 py-12 text-center text-sm text-[#64748b]">
                    No trips found for current filters.
                  </div>
                ) : (
                  <>
                    <div className="grid gap-8 md:grid-cols-2">
                      {travelPageItems.map((item) => (
                        <article
                          key={item.id}
                          className="overflow-hidden rounded-2xl border border-[#eef2f6] bg-white shadow-[0_18px_40px_rgba(15,23,36,0.08)]"
                        >
                          <div className="relative h-56">
                            <img
                              src={item.image}
                              alt={item.title}
                              className="h-full w-full object-cover"
                            />
                            {item.badge && (
                              <span className="absolute right-4 top-4 rounded-full bg-[#f7b500] px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                                {item.badge}
                              </span>
                            )}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-md bg-white px-4 py-1 text-[11px] text-[#6b7280] shadow-[0_6px_16px_rgba(15,23,36,0.12)]">
                              {item.durationLabel}
                            </div>
                          </div>

                          <div className="flex h-full flex-col gap-3 px-6 py-6">
                            <div>
                              <h4 className="text-lg font-semibold text-[#111827]">
                                {item.title}
                              </h4>
                              <p className="text-xs text-[#9aa3af]">
                                {item.country}
                              </p>
                            </div>
                            <p className="text-xs leading-relaxed text-[#6b7280]">
                              {item.summary}
                            </p>
                            <button
                              type="button"
                              className="mt-2 w-full rounded-lg bg-[#f7b500] px-4 py-2 text-xs font-semibold text-white shadow-[0_10px_20px_rgba(247,181,0,0.35)]"
                              onClick={() => onSelectPackage(item.id)}
                            >
                              View Details
                            </button>
                            <div className="mt-2 border-t border-[#eef2f6] pt-4">
                              <div className="text-right">
                                <p className="text-[10px] uppercase text-[#9aa3af]">
                                  From
                                </p>
                                <p className="text-base font-semibold text-[#111827]">
                                  {item.price}{" "}
                                  {item.oldPrice && (
                                    <span className="text-xs font-normal text-[#9aa3af] line-through">
                                      {item.oldPrice}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>

                    <div className="mt-auto flex items-center justify-center gap-3 pt-10">
                      {Array.from({ length: travelTotalPages }, (_, index) => {
                        const pageNumber = index + 1;
                        const isActive = pageNumber === travelPage;
                        return (
                          <button
                            key={pageNumber}
                            type="button"
                            onClick={() => setTravelPage(pageNumber)}
                            aria-current={isActive ? "page" : undefined}
                            className={`flex h-6 w-6 items-center justify-center rounded text-[11px] font-semibold transition ${
                              isActive
                                ? "cursor-default bg-[#f7b500] text-[#111827]"
                                : "bg-[#0b1c2b] text-white hover:bg-[#122746]"
                            }`}
                            disabled={isActive}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
